/**
 * Autorisatie — multi-functie rollen, permissie-templates + per-user overrides.
 *
 * Conform docs/specs/21-autorisatie-navigatie-werkruimtes.md §1/§2/§Stap 2.
 * TypeScript-mirrors van de Prisma-enums + DTO's + Zod-schemas. De effectieve
 * permissie-berekening (`computeEffectivePermissions`) en API-routes komen in
 * PR #D; deze module levert de gedeelde contracten.
 *
 * `/me/permissions` blijft ongewijzigd (spec §regels 1) — de ModulePermission-
 * tabel blijft de single source of truth voor runtime-checks.
 */

import { z } from 'zod'

// Module-keys worden hier als `string` getypeerd (niet de smalle `ModuleType`
// uit ./index): de autorisatie-laag werkt met de VOLLEDIGE Prisma
// `ModuleType`-enum (45 modules incl. CONSIGNATIE/ACQUISITIE/EXPORT/…), terwijl
// `./index#ModuleType` een kleinere legacy-subset is. De API valideert de
// module-waarde server-side tegen de Prisma-enum (Zod `z.string()` →
// Prisma type-check), dus runtime blijft strikt. Conform spec 21 §8/§9 — de
// admin-matrix toont álle modules die een template/override hebben.
export type ModulePermissionKey = string

// ─── Enum-mirrors (Prisma → TS) ──────────────────────────────────────────────

/// Functie-typen (spec 21 §1). Mirror van Prisma `FunctionType`.
export const FUNCTION_TYPES = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR_MEDEWERKER',
  'SALARISADMIN',
  'MANAGER',
  'ADMIN',
  'KLANT_BEHEERDER',
] as const

export type FunctionType = (typeof FUNCTION_TYPES)[number]

/// Permissie-vlaggen (spec 21 §2). Mirror van Prisma `PermissionKey`.
export const PERMISSION_KEYS = ['canView', 'canEdit', 'canDelete', 'canApprove', 'canInvoice'] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

/// Override-richting (spec 21 §2). Mirror van Prisma `OverrideAction`.
export const OVERRIDE_ACTIONS = ['GRANT', 'REVOKE'] as const

export type OverrideAction = (typeof OVERRIDE_ACTIONS)[number]

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface UserFunctionDTO {
  function: FunctionType
  isPrimary: boolean
}

export interface FunctionPermissionTemplateDTO {
  function: FunctionType
  module: ModulePermissionKey
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  canInvoice: boolean
}

export interface UserPermissionOverrideDTO {
  id: string
  userId: string
  module: ModulePermissionKey
  permission: PermissionKey
  action: OverrideAction
  reason: string | null
  grantedBy: string
  grantedByName: string | null
  createdAt: string
}

/// Per-cel herkomst in de effectieve-permissie-matrix (spec 21 §8 kleurcodering):
/// - `template`  → groen  (via functie-template)
/// - `grant`     → blauw  (via GRANT-override)
/// - `revoke`    → rood doorgestreept (via REVOKE-override)
/// - `none`      → grijs  (geen toegang)
export type EffectivePermissionSource = 'template' | 'grant' | 'revoke' | 'none'

export interface EffectivePermissionCellDTO {
  granted: boolean
  source: EffectivePermissionSource
}

/// Volledige matrix (module × permission) voor de admin-preview (spec 21 §8/§9).
export interface EffectivePermissionMatrixRowDTO {
  module: ModulePermissionKey
  permissions: Record<PermissionKey, EffectivePermissionCellDTO>
}

export interface EffectivePermissionMatrixDTO {
  userId: string
  functions: FunctionType[]
  rows: EffectivePermissionMatrixRowDTO[]
}

// ─── Zod-schemas (admin-mutaties, spec 21 §Stap 4) ───────────────────────────

const functionEnum = z.enum(FUNCTION_TYPES)
const permissionEnum = z.enum(PERMISSION_KEYS)
const overrideActionEnum = z.enum(OVERRIDE_ACTIONS)

/// `PUT /admin/users/:id/functions` — vervangt de volledige functie-set van een
/// user. `isPrimary` markeert de default-werkruimte; exact één primary wordt
/// server-side afgedwongen.
export const userFunctionsUpdateSchema = z.object({
  functions: z
    .array(
      z.object({
        function: functionEnum,
        isPrimary: z.boolean().default(false),
      }),
    )
    .max(FUNCTION_TYPES.length),
})

export type UserFunctionsUpdateInput = z.infer<typeof userFunctionsUpdateSchema>

/// `PUT /admin/functions/:type/permissions` — vervangt de permissie-template
/// van één functie (lijst module-flag-rijen).
export const functionPermissionTemplateUpdateSchema = z.object({
  permissions: z.array(
    z.object({
      module: z.string().min(1),
      canView: z.boolean().default(false),
      canEdit: z.boolean().default(false),
      canDelete: z.boolean().default(false),
      canApprove: z.boolean().default(false),
      canInvoice: z.boolean().default(false),
    }),
  ),
})

export type FunctionPermissionTemplateUpdateInput = z.infer<
  typeof functionPermissionTemplateUpdateSchema
>

/// `POST /admin/users/:id/overrides` — voeg een override toe. REVOKE vereist een
/// reden (spec 21 §2 + §8). De `.superRefine` dwingt dat af.
export const userPermissionOverrideCreateSchema = z
  .object({
    module: z.string().min(1),
    permission: permissionEnum,
    action: overrideActionEnum,
    reason: z.string().max(500).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.action === 'REVOKE' && (!val.reason || val.reason.trim().length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'Reden is verplicht bij een REVOKE-override.',
      })
    }
  })

export type UserPermissionOverrideCreateInput = z.infer<typeof userPermissionOverrideCreateSchema>
